import { gql } from '@apollo/client';
import { USER_NAME_FIELDS } from 'src/models/users/queries';

export const CODE_FIELDS = gql`
  fragment codeFields on Code {
    code
  }
`;

export const KEYWORD_BASE_FIELDS = gql`
  fragment keywordsBaseFields on Keyword {
    id
    value
  }
`;

export const KEYWORD_FIELDS = gql`
  fragment keywordsFields on Keyword {
    ...keywordsBaseFields
    searchType
    searchLogic
    filterLogic
    highlightColor
  }
  ${KEYWORD_BASE_FIELDS}
`;

export const MP_FIELDS = gql`
  fragment matchingProfileFields on MatchingProfile {
    id
    name
    owners {
      ...userNameFields
    }
    subscribers {
      ...userNameFields
    }
    accessToEdit
    accessControl {
      editPolicy
      subscribePolicy
    }
    bTagIds
    nutsCodes
    nutsCodesExact
    cpvCodes
    cpvCodesExact
    buyerOrgIds
    buyerSearchStrings
    keywords {
      ...keywordsFields
    }
    profileType
    supplierSearchTerms {
      id
      searchType
      status
      value
    }
    supplierSearchLogic
    buyerTypes
    contractValueCurrency
    contractValueMin
    contractValueMax
    contractValueIncludeMissingValue
    companies @client
  }
  ${KEYWORD_FIELDS}
  ${USER_NAME_FIELDS}
`;

export const UPDATE_MP_POLICY = gql`
  mutation updateMatchingProfile($id: String!, $policy: AccessControlPolicy!) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { accessControl: { subscribePolicy: $policy } }) {
      id
      accessControl {
        editPolicy
        subscribePolicy
      }
    }
  }
`;

export const UPDATE_MP_OWNERS_POLICY = gql`
  mutation updateMatchingProfile($id: String!, $policy: AccessControlPolicy!) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { accessControl: { editPolicy: $policy } }) {
      id
      accessControl {
        editPolicy
        subscribePolicy
      }
    }
  }
`;

export const UPDATE_MP_OWNERS = gql`
  mutation updateMatchingProfile($id: String!, $owners: [String!]) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { owners: $owners, accessControl: { editPolicy: ONLY_ADDED } }) {
      id
      owners {
        ...userNameFields
      }
      subscribers {
        ...userNameFields
      }
    }
  }
  ${USER_NAME_FIELDS}
`;

export const UPDATE_MP_SUBSCRIBERS = gql`
  mutation updateMatchingProfile($id: String!, $subscribers: [String!]) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { subscribers: $subscribers }) {
      id
      subscribers {
        ...userNameFields
      }
      owners {
        ...userNameFields
      }
    }
  }
  ${USER_NAME_FIELDS}
`;

export const MOVE_MP_MEMBERS = gql`
  mutation updateMatchingProfile($id: String!, $owners: [String!], $subscribers: [String!]) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { subscribers: $subscribers, owners: $owners }) {
      id
      subscribers {
        ...userNameFields
      }
      owners {
        ...userNameFields
      }
    }
  }
  ${USER_NAME_FIELDS}
`;

export const SEARCH_REGIONS = gql`
  query searchNutCodes($input: CodesSearchDTO!) {
    searchNutCodes(input: $input) {
      ...codeFields
      children {
        ...codeFields
        children {
          ...codeFields
          children {
            ...codeFields
            children {
              ...codeFields
              children {
                ...codeFields
              }
            }
          }
        }
      }
    }
  }
  ${CODE_FIELDS}
`;

export const GET_NUTS_FILTER = gql`
  query getNutsFilter {
    getNutsFilter {
      codes: nutsCodes
      codesExact: nutsCodesExact
    }
  }
`;

export const UPDATE_MP_NUTS_CODES = gql`
  mutation updateMatchingProfile($id: String!, $nutsCodes: [String!], $nutsCodesExact: [String!]) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { nutsCodes: $nutsCodes, nutsCodesExact: $nutsCodesExact }) {
      id
      nutsCodes
      nutsCodesExact
    }
  }
`;

export const UPDATE_MP_CPV_CODES = gql`
  mutation updateMatchingProfile($id: String!, $cpvCodes: [String!], $cpvCodesExact: [String!]) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { cpvCodes: $cpvCodes, cpvCodesExact: $cpvCodesExact }) {
      id
      cpvCodes
      cpvCodesExact
    }
  }
`;

export const SEARCH_CPV_CODES = gql`
  query searchCpvCodes($input: CodesSearchDTO!) {
    searchCpvCodes(input: $input) {
      ...codeFields
      children {
        ...codeFields
        children {
          ...codeFields
          children {
            ...codeFields
            children {
              ...codeFields
              children {
                ...codeFields
                children {
                  ...codeFields
                  children {
                    ...codeFields
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${CODE_FIELDS}
`;

export const UPDATE_MP_BTAGS = gql`
  mutation updateMatchingProfile($id: String!, $bTagIds: [String!]) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { bTagIds: $bTagIds }) {
      id
      bTagIds
    }
  }
`;

export const CREATE_MULTIPLE_MP_KEYWORD = gql`
  mutation createMultipleKeywords($matchingProfileId: String!, $keywords: [String!]!) {
    createMultipleKeywords(input: { matchingProfileId: $matchingProfileId, keywords: $keywords }) {
      ...keywordsFields
    }
  }
  ${KEYWORD_FIELDS}
`;

export const UPDATE_MP_KEYWORD = gql`
  mutation updateKeyword(
    $id: String!
    $value: String
    $searchType: KeywordsSearchType
    $filterLogic: KeywordsFilterLogic
    $searchLogic: KeywordsSearchLogic
    $highlightColor: KeywordsHighlightColor
  ) {
    updateKeyword(
      input: {
        id: $id
        value: $value
        searchType: $searchType
        filterLogic: $filterLogic
        searchLogic: $searchLogic
        highlightColor: $highlightColor
      }
    ) {
      ...keywordsFields
    }
  }
  ${KEYWORD_FIELDS}
`;

export const DELETE_MP_KEYWORD = gql`
  mutation deleteKeyword($id: String!) {
    deleteKeyword(id: $id)
  }
`;

export const UPDATE_MP_BUYERS = gql`
  mutation updateMatchingProfile($id: String!, $buyerOrgIds: [String!], $buyerSearchStrings: [String!]) {
    updateMatchingProfile(
      id: $id
      valuesToBeUpdated: { buyerOrgIds: $buyerOrgIds, buyerSearchStrings: $buyerSearchStrings }
    ) {
      id
      buyerOrgIds
      buyerSearchStrings
    }
  }
`;

export const UPDATE_MP_BUYER_TYPES = gql`
  mutation updateMatchingProfile($id: String!, $buyerTypes: [BuyerType!]) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { buyerTypes: $buyerTypes }) {
      id
      buyerTypes
    }
  }
`;

export const UPDATE_MP_CONTRACT_VALUE = gql`
  mutation updateMatchingProfile(
    $id: String!
    $contractValueRange: ContractValueRangeDTO
    $includeMissingValue: Boolean
  ) {
    updateMatchingProfile(
      id: $id
      valuesToBeUpdated: { contractValue: $contractValueRange, contractValueIncludeMissingValue: $includeMissingValue }
    ) {
      id
      contractValueCurrency
      contractValueMin
      contractValueMax
      contractValueIncludeMissingValue
    }
  }
`;

export const LOAD_BTAGS = gql`
  query getBTags {
    getBTags {
      id
      name
      description
    }
  }
`;

export const LOAD_ALL_MY_MP = gql`
  query getAllMyMatchingProfiles($input: GetMatchingProfileDTO) {
    getAllMyMatchingProfiles(input: $input) {
      ...matchingProfileFields
    }
  }
  ${MP_FIELDS}
`;

export const LOAD_ALL_MY_MP_IDS = gql`
  query getAllMyMatchingProfiles($input: GetMatchingProfileDTO) {
    getAllMyMatchingProfiles(input: $input) {
      id
      name
      profileType
    }
  }
`;

export const LOAD_MP = gql`
  query getMatchingProfile($id: String!) {
    getMatchingProfile(id: $id) {
      ...matchingProfileFields
    }
  }
  ${MP_FIELDS}
`;

export const LOAD_MP_KEYWORDS = gql`
  query getMatchingProfile($id: String!) {
    getMatchingProfile(id: $id) {
      id
      name
      keywords {
        ...keywordsBaseFields
      }
    }
  }
  ${KEYWORD_BASE_FIELDS}
`;

export const CREATE_MP = gql`
  mutation createMatchingProfile(
    $name: String!
    $profileType: ProfileType
    $buyerOrgIds: [String!]
    $buyerSearchStrings: [String!]
    $keywords: [NewKeywordDTO!]
    $supplierOrgIds: [SupplierOrgIdEntry!]
    $supplierSearchStrings: [SupplierSearchStringEntry!]
    $supplierSearchLogic: JunctionLogic
    $nutsCodes: [String!]
    $nutsCodesExact: [String!]
    $cpvCodes: [String!]
    $cpvCodesExact: [String!]
    $buyerTypes: [BuyerType!]
    $contractValue: ContractValueRangeDTO
    $contractValueIncludeMissingValue: Boolean
  ) {
    createMatchingProfile(
      initialValues: {
        name: $name
        profileType: $profileType
        buyerOrgIds: $buyerOrgIds
        buyerSearchStrings: $buyerSearchStrings
        keywords: $keywords
        supplierOrgIds: $supplierOrgIds
        supplierSearchStrings: $supplierSearchStrings
        supplierSearchLogic: $supplierSearchLogic
        nutsCodes: $nutsCodes
        nutsCodesExact: $nutsCodesExact
        cpvCodes: $cpvCodes
        cpvCodesExact: $cpvCodesExact
        buyerTypes: $buyerTypes
        contractValue: $contractValue
        contractValueIncludeMissingValue: $contractValueIncludeMissingValue
      }
    ) {
      ...matchingProfileFields
    }
  }
  ${MP_FIELDS}
`;

export const DELETE_MP = gql`
  mutation deleteMatchingProfile($id: String!) {
    deleteMatchingProfile(id: $id)
  }
`;

export const UPDATE_MP_NAME = gql`
  mutation updateMatchingProfile($id: String!, $name: String!) {
    updateMatchingProfile(id: $id, valuesToBeUpdated: { name: $name }) {
      id
      name
    }
  }
`;

export const UPDATE_MP = gql`
  mutation updateMatchingProfile(
    $id: String!
    $supplierOrgIds: [SupplierOrgIdEntry!]
    $supplierSearchStrings: [SupplierSearchStringEntry!]
    $supplierSearchLogic: JunctionLogic
    $buyerOrgIds: [String!]
    $buyerSearchStrings: [String!]
    $buyerTypes: [BuyerType!]
    $keywords: [NewKeywordDTO!]
    $contractValueRange: ContractValueRangeDTO
    $contractValueIncludeMissingValue: Boolean
    $nutsCodes: [String!]
    $nutsCodesExact: [String!]
    $cpvCodes: [String!]
    $cpvCodesExact: [String!]
  ) {
    updateMatchingProfile(
      id: $id
      valuesToBeUpdated: {
        supplierOrgIds: $supplierOrgIds
        supplierSearchStrings: $supplierSearchStrings
        supplierSearchLogic: $supplierSearchLogic
        buyerOrgIds: $buyerOrgIds
        buyerSearchStrings: $buyerSearchStrings
        buyerTypes: $buyerTypes
        keywords: $keywords
        contractValue: $contractValueRange
        contractValueIncludeMissingValue: $contractValueIncludeMissingValue
        nutsCodes: $nutsCodes
        nutsCodesExact: $nutsCodesExact
        cpvCodes: $cpvCodes
        cpvCodesExact: $cpvCodesExact
      }
    ) {
      ...matchingProfileFields
    }
  }
  ${MP_FIELDS}
`;
